import { Box, Fab, Grid, InternalStandardProps, Theme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { AppHeader } from "JS/React/Components/AppHeader";
import { useEffect, useMemo, useState } from "react";
import { useGlobalPlaylists } from "JS/React/Hooks/Playlist";
import { useNavigate, useParams } from "react-router-dom";
import { useRouting } from "JS/React/Hooks/Routes";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { messaging } from "JS/Helpers/UserMessaging";
import { getDownloadedAudios } from "JS/Helpers";
import { useGlobalAudios } from "JS/React/Hooks/Audio";
import { useGlobalGifts } from "JS/React/Hooks/Gifts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
    Content,
    EventActions,
    EventCategories,
    EventNames,
    ReceivedGift,
} from "JS/Models";
import { useFirebaseLogger } from "JS/React/Hooks/Firebase";
import { useGetFavorites, useGlobalFavorites } from "JS/React/Hooks/Favorites";
import { ListingComponent } from "../../Audios/ListingComponent";

const FabContainer = styled(Box)(({ theme }) => ({
    position: "fixed",
    right: "10px",
    bottom: "30px",
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    zIndex: 2,
}));

const StyledFab = styled(Fab)(({ theme }) => ({
    background: "#808080",
    "&:hover": {
        background: "#808080",
    },
}));

const StyledList = styled(Grid)(({ theme }) => ({
    overflow: "auto",
    width: "100%",
    height: `100%`,
}));

export interface PlaylistDetailProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {}

export const PlaylistDetail = (props: PlaylistDetailProps) => {
    const { className } = props;
    const { userPlaylists: playlists, playlistResumeState } =
        useGlobalPlaylists();
    const params: any = useParams();
    const navigate = useNavigate();
    const { linkProvider } = useRouting();
    const { id } = params;

    const {
        audios: allAudios,
        resumeUserAudios,
        resumeUserBundles,
    } = useGlobalAudios();
    const [audios, setAudios] = useState<PlaylistAudioContent[]>([]);

    // download playlist
    const { receivedMemberAudios, receivedProspectAudios } = useGlobalGifts();

    const { favNids } = useGlobalFavorites();

    useGetFavorites(!!favNids?.length);

    const { logFirebaseEvent } = useFirebaseLogger();

    const playlist = useMemo(() => {
        return playlists?.find((p) => p.id === +id);
    }, [playlists]);

    useEffect(() => {
        if (playlist)
            logFirebaseEvent(EventNames.AUDIO_PLAYLIST_OPENED, {
                action: EventActions.OPEN,
                category: EventCategories.AUDIO_PLAYLIST,
                playlistId: playlist.id.toString(),
            });
    }, [playlist?.id]);

    const preparePlaylistAudios = async () => {
        const audiosInPlaylist = playlist?.audios
            ?.map((a) => a)
            ?.sort((a, b) => +a.sortOrder - +b.sortOrder)
            ?.map((a) => {
                const selfAudio = allAudios.find((aa) => aa.nid === a.nid);
                if (selfAudio) return { ...selfAudio, ...a };
                const memberGiftAudio = receivedMemberAudios.find(
                    (rmg) => rmg.nid === a.nid,
                );
                if (memberGiftAudio) return { ...memberGiftAudio, ...a };
                return {
                    ...receivedProspectAudios.find((rgp) => rgp.nid === a.nid),
                    ...a,
                };
            })
            .filter((a) => a !== undefined);

        await getDownloadedAudios(audiosInPlaylist)
            .then((res) => {
                if (res?.length) {
                    const data = audiosInPlaylist.map((a) => {
                        const downloadedContent = res?.find(
                            (r) => r.nid === a.nid,
                        );
                        let audioObj = {};
                        if (downloadedContent) {
                            audioObj = {
                                ...downloadedContent,
                                isDownloaded: true,
                            };
                        } else {
                            audioObj = {
                                ...a,
                                isDownloaded: false,
                            };
                        }
                        return audioObj as PlaylistAudioContent;
                    });
                    setAudios(data);
                }
            })
            .catch((err) => {
                console.log({ err });
            });
    };
    useEffect(() => {
        preparePlaylistAudios();
    }, [playlist, allAudios, receivedMemberAudios, receivedProspectAudios]);

    const loading = false;

    const isResumeable = (value: Content | ReceivedGift) => {
        if (+value.no_of_files > 1) {
            let filterResumeBundle = resumeUserBundles?.filter(
                (x) => x.nid === value.nid,
            );
            return filterResumeBundle?.length > 0;
        } else {
            if (
                resumeUserAudios &&
                resumeUserAudios.filter((x) => x.nid === value.nid).length > 0
            ) {
                const resumeData = resumeUserAudios.find(
                    (x) => x.nid === value.nid,
                );
                return !!resumeData;
            } else {
                return false;
            }
        }
    };

    const playPlaylist = () => {
        logFirebaseEvent(EventNames.AUDIO_PLAYLIST_PLAYED, {
            category: EventCategories.AUDIO_PLAYLIST,
            action: EventActions.PLAY,
            playlistId: id,
        });
        navigate(
            linkProvider.react
                .offline()
                .playlist()
                .play(id, {
                    index: playlistResumeState(
                        playlist,
                        audios,
                    )?.resumeAudioIndex?.toString(),
                    bundleIndex: playlistResumeState(
                        playlist,
                        audios,
                    )?.resumeBundleIndex?.toString(),
                }),
        );
    };
    const onItemClick = (value, _, itemIndex: number) => {
        navigate(
            linkProvider.react
                .offline()
                .playlist()
                .play(id, {
                    index: audios?.indexOf(value).toString(),
                    bundleIndex: "0",
                    scrollTo: value.nid,
                }),
        );
    };
    return (
        <>
            <AppHeader
                title={playlist?.name}
                canGoBack
                searchable={false}
                onBackClick={() => {
                    navigate(
                        linkProvider.react.offline().playlist().index(),
                    );
                }}
            />
            <FabContainer className={className}>
                <StyledFab
                    color="success"
                    aria-label="add"
                    onClick={playPlaylist}
                >
                    <FontAwesomeIcon icon={["fas", "play"]} />
                </StyledFab>
            </FabContainer>
            {loading && (
                <AppBackdropProgress
                    open={true}
                    backdropText={messaging.loader.fetch_playlist}
                />
            )}
            {!loading && (
                <StyledList>
                    <ListingComponent
                        listContent={audios}
                        showSortHeader={false}
                        isResumeable={isResumeable}
                        favoritesContent={favNids}
                        handleListItemClick={onItemClick}
                        controlType="offlinePlaylist"
                    />
                </StyledList>
            )}
        </>
    );
};

export type PlaylistAudioContent =
    | (Content | ReceivedGift) & {
          isDownloaded: boolean;
      };
